import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { Link, useHistory } from "react-router-dom";
import AuthenticationService from "../../services/AuthService";
import { CacheContext } from "../../contexts/Cache/CacheStateProvider";
import CheckAccess from '../Roles/CheckAccess';
import { EditLocaleIco } from '../Locale/LocaleIcon';
import useModal from "../../hooks/useModal";
import UserAccessComponents from "../Roles/UserAccessComponents";
import { LocalizeContext } from "../../contexts/Localize/LocalizeStateProvider";
import { HelpIcon } from "../Help/HelpSidePanel";
import { PushNotificationPopup } from "../PushNotification/PushNotificationPage";
import { AppDownload } from "../Common/AddToHomeScreen";
import { DataSources } from "../../constants/DataSourceList";
import { usePageLocale } from "../../hooks/useLocale";
import DataSourceService from "../../services/DataSourceService";
import { useCurrentUserHook } from "../Authentication/useUserHook";
import { useRoleAccessHook } from "../Roles/useRoleAccessHook";
import { useSubscriptionAccess } from '../Subscriptions/useSubscription';
import { useMenuItemProps } from './useMenuItemProps';
import OrgUiSettingsForm from '../Organizations/UiSettings/UiSettingForm';
import GenericModal from '../Modals/GenericModal';

const SideMenu = (props) => {
  const {currentUser, dispatch} = useCurrentUserHook();
  const {cacheState, cacheDispatch} = useContext(CacheContext);
  const {localeState:{sideMenuList, digitalContent, trainingMenu}, localeDispatch} = useContext(LocalizeContext);

  const uiSettingsFormId = 1122;

  let { current: scope } = useRef({});
  const sideNavEl = useRef(null);
  const sideNavMenuEl = useRef(null);
  const history = useHistory();
  const isDevAdmin = CheckAccess.isDevUser(currentUser.email);

  const [sideMenu, setSideMenu] = useState();
  const [loading, setLoading] = useState(true);
  
  const { checkMenuAccess } = useRoleAccessHook();  
  const { isOpen: isPushSettingOpen, toggleModal:togglePushSetting } = useModal();
  const { isOpen: isUiSettingsOpen, toggleModal: toggleUiSettingsModal } = useModal();
  
  const {labelLocale:topNavLocale} = usePageLocale(currentUser.current_locale, 'sideMenuTopNav');

  const orgLogo = currentUser?.current_organization?.data?.picture || 'images/logo/cuedlink_logo.svg';
  const userRole = currentUser?.current_member?.role_names;

  const {subscriptionAccesses} = useSubscriptionAccess();
  const {side_menu:sideMenuDs} = subscriptionAccesses || {};

  useEffect(() =>{
    setSideMenu(sideMenuList?.options || {});
  }, [sideMenuList])

  const signout = (e) => {
    e.stopPropagation();
    e.preventDefault();
    AuthenticationService.signout().then(() => {
      localStorage.removeItem("authorization");
      cacheDispatch({
        "type": "UPDATE",
        "payload": {}
      })
      dispatch({ "type": "LOGOUT" });
    })
  }

  useEffect(() => {
    getSideMenuDs();
    document.addEventListener("keydown", handleEscape);
    document.addEventListener("click", handleClick, true);

    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("click", handleClick, true);
    }
  }, [currentUser.current_locale])

  const handleEscape = (e) => {
    if (e.keyCode === 27) props.setOpenSideNav(false);
  }

  /*const handleClick = (e) => {
    if (
      !currentUser.edit_locale && (e.target.id !== 'edit_locale')
      && sideNavEl.current 
      && (sideNavEl.current.contains(e.target) || sideNavMenuEl.current.contains(e.target))
    ) {
      props.setOpenSideNav(false);
    }
  }*/

  const handleClick = (e) => {
    if (sideNavMenuEl.current && sideNavMenuEl.current.contains(e.target)){
      close();
    }
  }

  const close = (e) => {
    e && e.preventDefault();
    props.setOpenSideNav(false);
  }

  const checkAccess = (menuType) => CheckAccess.checkOrgAccess(currentUser, menuType);
  const checkTrainingAccess = (menuType) => CheckAccess.checkTrainingAccess(currentUser, menuType);

  const editLocale = () =>{
    currentUser.edit_locale = !currentUser.edit_locale;
    dispatch({
      type: "UPDATE",
      payload: { user: currentUser }
    });
  }
  
  const routeOrgProfile = (e) =>{
    e.preventDefault();
    const userOrg = currentUser.organizations.find((o)=> o.created_by === currentUser.id);
    if(userOrg){
      history.push(`/profile/${userOrg.id}`);
    }
  }

  const getSideMenuDs = () =>{
    setLoading(true)
    DataSourceService.getDataSource({
      'data_source_params':{
        'data_source_id':[
          DataSources.sideMenuDsId, 
          DataSources.headerDsId, 
          DataSources.digitalContentMenu, 
          DataSources.confirmMenuDsId, 
          DataSources.sideMenuTopNav,
          4083, 
          4082,
          3886,
          4830,
          4603,
          4605,
          4836
        ],
        'response_type':'object', 
        'group_by':'sid',
        'locale':currentUser.current_locale,
        'per_page' : 30
      }
    }).then((data)=>{
      if(data){
        localeDispatch({
          'type': "UPDATE",
          'payload': {
            'sideMenuList':(data.section_menu_nav_bar),
            'digitalContent':(data.menu_digital_content?.options || {}),
            'headerFooter':(data.locale_header_footer),
            'confirmMenu':(data.delete_confirm),
            'sideMenuTopNav':(data.side_menu_top_nav),
            'journeyStatus':Object.values(data.status_journey_case.options),
            'statusCloseReasons':Object.values(data.status_close_reason.options),
            'statusInactiveReasons':Object.values(data.status_inactive_reason.options),
            'languages':Object.values(data.languages.options),
            'cookieNotice':data.cookie_notice,
            'trainingMenu':data.training_menu,
            'commonPageLocale':data.common_page_locale
          }
        })
        setLoading(false)
      }
    })
  }

  const onLocaleUpdate = (menu) =>{
    sideMenu[menu.sid] = menu;
    setSideMenu({...sideMenu});
  }

  const openUiSettingsForm = (e) => {
    e.preventDefault();
    scope.formProps = {
      isPopupMode: true,
      match: {
        params: {
          id: null,
          formId: uiSettingsFormId,
        },
      },
    };
    toggleUiSettingsModal();
  }

  const {menuItemProps} = useMenuItemProps({
    openUiSettingsForm
  })

  return (
    <div id="menu-main" ref={sideNavEl}
      className={`menu menu-box-left menu-box-detached round-medium ${props.openSideNav ? "menu-active" : ""} `}
      style={{ width: 260, opacity: 1, display: "block" }}>
      <div className="menu-icons pointer">
        <span onClick={()=>window.location.reload()}>
          <i className="fas fa-refresh color-black"/>
        </span>
        <span onClick={() => {props.toggleOrgModal();close();}}>
          <i className="fas fa-building color-green1-dark" />
        </span>
        <span>
          <i className="fas fa-sitemap color-orange-dark" />
        </span>
        <Link to="/user/profile" onClick={() => close()}>
          <i className="fas fa-cog color-black"/>
        </Link>
        <span className="close-menu" onClick={(e) => close(e)}>
          <i className="fas fa-times color-red2-dark " />
        </span>
        <div className="clear" />
      </div>

      <div className="menu-items">
        <div className="p-10 brb-lgrey">
          <div className="font-13 p-l-15 m-t-5 pointer black-light-1">
            <div className="m-b-15" data-tip="Language Menu" 
              onClick={() => {props.toggleLocaleModal();close();}}>
              <i className="fas fa-globe w-30 color-black"/>
              <span className="text-cap">
                {topNavLocale('1')} - {currentUser.current_locale}
              </span>
            </div>
            {/*<AppDownload label={topNavLocale(2)}/>*/}
            <div className="m-b-15 flex alignstart" data-tip="Signed in as">
              <i className="fas fa-user-tag w-30 color-black m-t-2" />
              <div className="flex coldir">
                <span>{currentUser.name}</span>
                {userRole &&
                  <span className="font-10 op-50 lh-unset">{userRole}</span>
                }
              </div>
            </div>
            <div className="m-b-15" data-tip="Orgnization Menu" 
              onClick={() => {props.toggleOrgModal();close();}}>
              <i className="fas fa-building w-30 color-black"/>
              <span>{currentUser?.current_organization?.data?.name}</span>
            </div>
            {false && 
              <span id="edit_locale" className="font-14" onClick={()=>editLocale()} data-tip="Switch Locale">
                <i className="fas fa-yin-yang w-30 color-black"/>
                <span id="edit_locale">Set language</span>
              </span>
            }
          </div>
          <div className="text-center">
            <img className="org-logo" src={orgLogo}/>
            <p className="m-t-10 font-10 op-50">
              Powered by Cuedlink
            </p>
          </div>
        </div>
      
        <div className="content m-b-10">
          {loading ?
            null
            :
            <div className="link-list link-list-1" ref={sideNavMenuEl}>
              {currentUser.current_subscription?.journeys 
                ? <MenuIcon currentLocale={currentUser.current_locale} 
                  menuItem={sideMenu.dashboard} onLocaleUpdate={onLocaleUpdate} 
                  iconCss="fas fa-home color-black" label="Home" sid="dashboard"
                  link="/ecn/table"/>
                : null
              }

              {sideMenuDs?.options?.map((m, i)=> checkMenuAccess(m.sid) && menuItemProps[m.sid] ?
                <MenuIcon key={i} currentLocale={currentUser.current_locale} 
                  menuItem={m} onLocaleUpdate={onLocaleUpdate} 
                  iconCss={`${menuItemProps[m.sid]?.icon} color-black`} 
                  label={m.label} sid={m.sid}
                  link={menuItemProps[m.sid]?.link}
                  onSelect={menuItemProps[m.sid]?.onSelect}/>
                : null
              )}

              {checkAccess('feedback') &&
                <MenuIcon currentLocale={currentUser.current_locale}
                  menuItem={sideMenu.feedback} onLocaleUpdate={onLocaleUpdate}
                  iconCss="far fa-question-circle color-black" label="Feedback"
                  sid="feedback" link="/list/feedback" />
              }

              <MenuIcon currentLocale={currentUser.current_locale} 
                menuItem={sideMenu.profile} onLocaleUpdate={onLocaleUpdate} 
                iconCss="fas fa-user color-black" label="Profile" sid="profile"
                onSelect={routeOrgProfile}/>

              <MenuIcon currentLocale={currentUser.current_locale} 
                menuItem={sideMenu.sign_out} onLocaleUpdate={onLocaleUpdate} 
                iconCss="fas fa-times color-red2-dark" label="Sign Out" sid="sign_out"
                onSelect={signout}/>
            </div>
          }

          {/*<PushNotificationPopup currentUser={currentUser}/>*/}

          <div className="center-text m-t-10">
            {/*<a href="#"
              className="icon icon-xxs round-tiny shadow-large m-b-0 bg-facebook">
              <i className="fab fa-facebook-f" />
            </a>
            <a href="#"
              className="icon icon-xxs round-tiny shadow-large m-b-0 bg-twitter">
              <i className="fab fa-twitter" />
            </a>
            <a href="#"
              className="icon icon-xxs round-tiny shadow-large m-b-0 bg-instagram">
              <i className="fab fa-instagram" />
            </a>
            <a href="#"
              className="icon icon-xxs round-tiny shadow-large m-b-0 bg-linkedin">
              <i className="fab fa-linkedin-in" />
            </a>
            <a href="#"
              className="icon icon-xxs round-tiny shadow-large m-b-0 bg-whatsapp">
              <i className="fab fa-whatsapp" />
            </a>*/}
            <p className="m-t-10 font-10 op-50">
              <i className="fas fa-copyright" aria-hidden="true"></i>
              {new Date().getFullYear()} IPIPAL INC. All Rights Reserved.
            </p>
          </div>

          {isUiSettingsOpen && (
            <GenericModal
              component={OrgUiSettingsForm}
              isOpen={isUiSettingsOpen}
              toggleModal={toggleUiSettingsModal}
              onSubmit={(form) => {
                toggleUiSettingsModal();
              }}
              {...scope.formProps}
            />
          )}

        </div>
      </div>
    </div>
  )
}

const MenuIcon = ({link, onSelect, iconCss, label, sid, onLocaleUpdate, menuItem, currentLocale}) => {
  
  const menuLocale = useMemo(() => {
    return {
      "label" : menuItem?.[currentLocale]?.label || menuItem?.label || label
    }
  }, [sid])

  if(link){
    return(
      <div className="flex justspacebetween aligncenter">
        <Link id={sid} to={link} className="w-82">
          <i className={iconCss} />
          <span>
            {menuLocale.label}
          </span>           
        </Link>
        <div className="">
          {menuItem ? 
            <EditLocaleIco localeType="data_source_data" 
              labelObj={menuItem} onLocaleUpdate={onLocaleUpdate}/> 
            : null
          }
          <HelpIcon recordIds={menuItem?.help_id} className="font-14 m-r-10"/>
          <i className="fas fa-angle-right r-ico"/>         
        </div>
      </div>
    )
  }else{
    return(
      <div className="flex justspacebetween aligncenter">
        <a id={sid} href="/" onClick={e=>onSelect(e)}  className="w-82">
          <i className={iconCss} />
          <span>{menuLocale.label}</span>
        </a>
        <div className="">
          {menuItem ? 
            <EditLocaleIco localeType="data_source_data"
              labelObj={menuItem} onLocaleUpdate={onLocaleUpdate}/> 
            : null
          }
          <HelpIcon recordIds={menuItem?.help_id} className="font-14 m-r-10"/>
          <i className="fas fa-angle-right r-ico"/>         
        </div>
      </div>
    )
  }
}

export default SideMenu;